import { Image, InputNumber, Select } from '@app/components'
import { InputNumberProps, SelectProps } from '@app/types'
import { cloneElement, isValidElement, ReactElement, ReactNode } from 'react'
import styled from 'styled-components'

const ICON_FIELD = 32
const ICON_MENU = 40
const FIELD_HEIGHT = 52

const TokenRow = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 0;
  margin: 0;
`

const TokenMark = styled.span<{ $size: number }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: ${(p) => p.$size}px;
  height: ${(p) => p.$size}px;
  flex-shrink: 0;
  line-height: 0;

  img,
  svg,
  .ant-image {
    display: block;
    width: ${(p) => p.$size}px;
    height: ${(p) => p.$size}px;
    object-fit: contain;
    margin: 0;
  }
`

const TokenCode = styled.span`
  font-size: 14px;
  font-weight: 600;
  line-height: 1.2;
  white-space: nowrap;
`

const TokenName = styled.span`
  font-size: 13px;
  color: rgba(0, 0, 0, 0.45);
  white-space: nowrap;
`

const TOKEN_NAMES: Record<string, string> = {
  PLS: 'PulseChain',
  wPLS: 'Wrapped PLS',
  HEX: 'HEX',
  eHEX: 'Ethereum HEX',
  PLSX: 'PulseX',
  WAIT: 'WAIT',
  INC: 'Incentive',
  PHLPv2: 'PHLP v2',
  TIDE: 'TIDE',
  GLORY: 'GLORY',
  CST: 'Coast',
  PRVX: 'ProveX',
  USD: 'US Dollar',
}

export const TokenLabel = ({
  flag,
  code,
  isCrypto,
  size = 'field',
}: {
  flag: ReactNode
  code: string
  isCrypto: boolean
  size?: 'field' | 'menu'
}) => {
  const px = size === 'menu' ? ICON_MENU : ICON_FIELD
  const iconStyle = { width: px, height: px, margin: 0, display: 'block' as const }
  const name = TOKEN_NAMES[code]
  return (
    <TokenRow>
      <TokenMark $size={px}>
        {isCrypto && isValidElement(flag) ? (
          cloneElement(flag as ReactElement<{ style?: React.CSSProperties }>, { style: iconStyle })
        ) : typeof flag === 'string' ? (
          <Image preview={false} src={flag} width={px} height={px} />
        ) : (
          flag
        )}
      </TokenMark>
      <TokenCode>{code}</TokenCode>
      {size === 'menu' && name ? <TokenName>{name}</TokenName> : null}
    </TokenRow>
  )
}

export const FieldStack = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  width: 100%;
`

export const LabelRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  align-items: center;
  gap: 8px;
  width: 100%;

  > :last-child {
    justify-self: end;
    text-align: right;
    white-space: nowrap;
  }
`

export const AmountRow = styled.div`
  display: grid;
  grid-template-columns: 3fr 1fr;
  align-items: stretch;
  gap: 8px;
  width: 100%;
  height: ${FIELD_HEIGHT}px;
  overflow: visible;
  position: relative;
  z-index: 2;
`

export const TokenStatic = styled.div`
  display: flex;
  align-items: center;
  height: ${FIELD_HEIGHT}px;
  padding: 0 12px;
  border: 1px solid #d9d9d9;
  border-radius: 6px;
  background: #fff;
`

export const TokenSelect = styled((props: SelectProps<any>) => (
  <Select
    {...props}
    virtual={false}
    popupMatchSelectWidth={false}
    listHeight={480}
    dropdownStyle={{ minWidth: 240, zIndex: 1100, ...props.dropdownStyle }}
    getPopupContainer={() => document.body}
  />
))`
  width: 100%;
  height: ${FIELD_HEIGHT}px;

  .ant-select-selector {
    display: flex;
    align-items: center;
    height: ${FIELD_HEIGHT}px;
  }

  .ant-select-selection-item {
    display: flex;
    align-items: center;
  }

  .ant-select-arrow {
    inset-inline-end: 11px;
  }
`

export const AmountInput = styled((props: InputNumberProps) => <InputNumber {...props} />)`
  width: 100%;
  height: ${FIELD_HEIGHT}px;

  .ant-input-number-input-wrap,
  .ant-input-number-input {
    height: ${FIELD_HEIGHT}px;
  }

  .ant-input-number-handler-wrap {
    opacity: 1;
    height: 100%;
  }
`
